import validator from 'validator';
export function useValidation() {

  const isMobile = () => {
    if(process.client) {
      return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    }
    return false;
  }
  // const isMobile = true;
  const isGuid = (testString) => {
    return testString != null && true && validator.isUUID(testString) && testString !== '00000000-0000-0000-0000-000000000000';
  }
  const isAdminOfClub = (player,clubId) => {
    return player.adminFor && player.adminFor.includes(clubId)
  }
  const isExpiredUnvalidatedPlayer = (player) =>{
    return player.userVerificationTokens.length > 0 && player.userVerificationTokens.every(_ => _.expired) && !isGuid(player.userId)
  }
  const isPendingUnvalidatedPlayer = (player) =>{
    return player.userVerificationTokens.some(_ => _.expired === false) && !isGuid(player.userId)
  }
  const isAdminControlledPlayer = (player) => {
    // A hidden player's stub carries no account either way, so it says nothing.
    return !player.obfuscated && !isGuid(player.userId) && (player.userVerificationTokens?.length ?? 0) === 0
  }
  const isPlayerInTeam = (playerId,team) => {
    if (!team || !team.players) return false;
    return team.players.some(_ => _.id === playerId)
  }
  const teamIdFromPlayerInFixture = (playerId,fixture) => {
    if (!fixture.awayTeam && !fixture.homeTeam) return null;
    if (fixture.awayTeam && isPlayerInTeam(playerId,fixture.awayTeam)) return fixture.awayTeamId;
    if (fixture.homeTeam && isPlayerInTeam(playerId,fixture.homeTeam)) return fixture.homeTeamId;
    return null;
  }
  const isPlayerInFixture = (playerId,fixture) => {
    if(fixture.awayTeam == null || fixture.homeTeam == null || !playerId) return false;
    return isPlayerInTeam(playerId,fixture.awayTeam) || isPlayerInTeam(playerId,fixture.homeTeam)
  }
  const isUserInTeam = (userId,team) => {
    return team.players.some(_ => _.userId === userId)
  }
  const isApprovedFixture = (fixture) => {
    return fixture.approvedAt != null
  }
  const fixtureNeedsApprovalFromPlayerId = (fixture,playerId) => {
    let teamId = teamIdFromPlayerInFixture(playerId,fixture);
    if(teamId == null) return false;
    if(teamId === fixture.homeTeamId && fixture.homeTeamApprovedAt == null) return true;
    if(teamId === fixture.awayTeamId && fixture.awayTeamApprovedAt == null) return true;
    return false;
  }
  // --- Canonical fixture result action (mirrors Flutter getFixtureDetailAction) ---
  // Single source of truth for "what should the score button say": Record / Approve / Edit.
  // True if the fixture carries any result (played or scored).
  const fixtureHasResult = (f) =>
    f != null && (f.score != null || f.playedAt != null ||
                  f.homeTeamResult != null || f.awayTeamResult != null);
  // Fully approved — mirrors Flutter Fixture.isFullyApproved(), which is BOTH per-side
  // timestamps being set (NOT approvedAt: the backend can stamp approvedAt when the result
  // is first submitted, i.e. one-sided, which would falsely read as fully approved). Falls
  // back to the other payload shapes only when the per-side timestamps aren't present.
  const isFixtureFullyApproved = (f) => {
    if (f == null) return true;
    if ('homeTeamApprovedAt' in f || 'awayTeamApprovedAt' in f)
      return f.homeTeamApprovedAt != null && f.awayTeamApprovedAt != null;
    if ('requiresApprovalFrom' in f) return f.requiresApprovalFrom == null;
    return f.approvedAt != null;
  }
  // Does THIS user still need to approve? teamIds is only needed for the
  // matrix summary shape (requiresApprovalFrom is a teamId).
  const fixtureRequiresMyApproval = (f, { playerId, isAdmin = false, teamIds = [] } = {}) => {
    if (f == null || isFixtureFullyApproved(f)) return false;
    if ('requiresApprovalFrom' in f && !('homeTeamApprovedAt' in f)) {   // matrix shape
      if (f.requiresApprovalFrom == null) return false;
      return teamIds.includes(f.requiresApprovalFrom) || isAdmin;
    }
    if (fixtureNeedsApprovalFromPlayerId(f, playerId)) return true;      // player: my side unapproved
    return isAdmin && fixtureHasResult(f);                               // admin can approve any unapproved result
  }
  // Canonical action: 'record' | 'approve' | 'edit' | null
  const fixtureResultAction = (f, ctx = {}) => {
    if (f == null) return null;
    // No score can exist until BOTH teams are set (tournament draw
    // placeholders arrive with null teams).
    if ((f.homeTeamId ?? f.homeTeam?.id) == null || (f.awayTeamId ?? f.awayTeam?.id) == null) return null;
    if (!fixtureHasResult(f)) return 'record';
    if (fixtureRequiresMyApproval(f, ctx)) return 'approve';
    return 'edit';
  }
  const FIXTURE_ACTION_LABELS = { record: 'Record result', approve: 'Approve result', edit: 'Edit result' };
  const FIXTURE_ACTION_ICONS  = { record: 'mdi-scoreboard-outline', approve: 'mdi-clipboard-check-outline', edit: 'mdi-pencil-outline' };
  const fixtureResultActionLabel = (action) => FIXTURE_ACTION_LABELS[action] ?? 'Edit result';
  const fixtureResultActionIcon  = (action) => FIXTURE_ACTION_ICONS[action]  ?? 'mdi-pencil-outline';
  const myTeamFromListOfTeams = (playerId,teams) => {
    return teams.find(_ => isPlayerInTeam(playerId,_))
  }

  // ---- Fixture schedule state -----------------------------------------------
  // Single source of truth for schedule state + wording. The API allows exactly
  // four states (a counter-proposal CLEARS the opponent's suggestion; suggesting
  // the opponent's date back CONFIRMS — FixtureService.UpdateScheduleWithContext):
  //   'none'          — nobody has suggested a date
  //   'minePending'   — my side's suggestion awaits the opponent
  //   'theirsPending' — the opponent's suggestion awaits ME (confirm/counter)
  //   'confirmed'     — scheduledAt set
  // 'pending' is the non-participant/admin view of a one-sided proposal.
  // The mobile app mirrors these in fixture_schedule_state.dart — change together.
  const fixtureScheduleState = (f, myTeamId = null) => {
    if (f?.scheduledAt != null) return 'confirmed';
    const homeSet = f?.homeTeamScheduledAt != null;
    const awaySet = f?.awayTeamScheduledAt != null;
    if (!homeSet && !awaySet) return 'none';
    const isHome = myTeamId != null && myTeamId === (f.homeTeamId ?? f.homeTeam?.id);
    const isAway = myTeamId != null && myTeamId === (f.awayTeamId ?? f.awayTeam?.id);
    if (!isHome && !isAway) return 'pending';
    return (isHome ? homeSet : awaySet) ? 'minePending' : 'theirsPending';
  }
  const FIXTURE_SCHEDULE_ACTION_LABELS = {
    none: 'Suggest time',
    minePending: 'Reschedule',
    theirsPending: 'Respond',
    confirmed: 'Reschedule',
    pending: 'Reschedule',
  };
  // noneLabel: grid surfaces (matrix) say 'Schedule'; card surfaces 'Suggest time'.
  const fixtureScheduleActionLabel = (state, noneLabel = null) =>
    state === 'none' && noneLabel != null ? noneLabel : (FIXTURE_SCHEDULE_ACTION_LABELS[state] ?? 'Suggest time');
  const isPlayersChallenge = (playerId,challenge) => {
    if(challenge == null || challenge.challengingTeam == null || challenge.challengedTeam == null) return false;
    return isPlayerInTeam(playerId,challenge.challengingTeam) || isPlayerInTeam(playerId,challenge.challengedTeam);
  }
    const isPlayerChallengerOfChallenge = (playerId,challenge) => {
        if(challenge == null || challenge.challengingTeam == null) return false;
        return isPlayerInTeam(playerId,challenge.challengingTeam);
    }
    const isPlayerChallengedInChallenge = (playerId,challenge) => {
        if(challenge == null || challenge.challengedTeam == null) return false;
        return isPlayerInTeam(playerId,challenge.challengedTeam);
    }

  return {
    isMobile,
    isGuid,
    isExpiredUnvalidatedPlayer,
    isPendingUnvalidatedPlayer,
    isAdminControlledPlayer,
    isPlayerInFixture,
    isUserInTeam,
    teamIdFromPlayerInFixture,
    fixtureNeedsApprovalFromPlayerId,
    isApprovedFixture,
    fixtureHasResult,
    isFixtureFullyApproved,
    fixtureRequiresMyApproval,
    fixtureResultAction,
    fixtureResultActionLabel,
    fixtureResultActionIcon,
    fixtureScheduleState,
    fixtureScheduleActionLabel,
    myTeamFromListOfTeams,
    isPlayersChallenge,
    isPlayerChallengerOfChallenge,
    isPlayerChallengedInChallenge,
    isAdminOfClub
  };
}

